// WhatsApp calls to a guest (POC, issue #95). Once an order is ready, a guest who registered on WhatsApp is called
// 5 minutes later if the order is still at the counter; staff can call at once from the row. The call itself goes out
// through RingRing's SIP server (infra/sip) and plays the recorded ready message. Meta only connects a business call
// to a guest who allowed it (a call-permission request sent after the WhatsApp registration).
// The call has its own state, separate from the order and from the message delivery: "answered" never means collected.
import { TicketError, type Channel, type TicketState } from './ticket';

/** Where the call is. `scheduled`: the automatic call waits for `at`. The others describe the latest attempt. */
export type CallStatus = 'scheduled' | 'calling' | 'answered' | 'not_answered' | 'failed' | 'no_permission';

/** Who started an attempt: the 5-minute timer or a staff tap. */
export type CallTrigger = 'auto' | 'staff';

export interface CallState {
  status: CallStatus;
  /** `scheduled`: when the automatic call goes out. Otherwise: when the latest attempt started. */
  at: number;
  /** Attempts started so far; a report from an older attempt is ignored. */
  attempt: number;
  trigger: CallTrigger | null;
  /** Hang-up cause (Q.850) or provider reason of a failed attempt, for the logs; never shown to the guest. */
  reason: string | null;
}

/** What the staff board carries about the call: no reason, no attempt counter. */
export interface CallView {
  status: CallStatus;
  at: number;
}

export const CALL = {
  /** The automatic call goes out this long after "Prête" if the order is still at the counter. */
  autoAfterReadyMs: 5 * 60_000,
  /** An attempt with no report from the SIP server after this long no longer blocks a new one. */
  staleAfterMs: 2 * 60_000,
  /** How long Meta lets the guest's phone ring before the SIP server gives up (seconds). */
  ringTimeoutSeconds: 40,
} as const;

/** A guest the SIP server can call: registered on WhatsApp with a number, order ready (the message says "ready"). */
export function canCallGuest(t: Pick<TicketState, 'status' | 'channel' | 'phone'>): boolean {
  return t.status === 'ready' && t.channel === 'whatsapp' && Boolean(t.phone);
}

/** The automatic call of an order marked ready at `readyAt`, or null when nobody can be called. */
export function scheduleAutoCall(t: Pick<TicketState, 'status' | 'channel' | 'phone'>, readyAt: number, previous: CallState | null = null): CallState | null {
  if (!canCallGuest(t)) return null;
  return { status: 'scheduled', at: readyAt + CALL.autoAfterReadyMs, attempt: previous?.attempt ?? 0, trigger: null, reason: null };
}

/** True while an attempt is under way and recent enough to trust: a second call would ring the guest twice. */
export function callInProgress(call: CallState | null | undefined, now: number): boolean {
  return call?.status === 'calling' && now - call.at < CALL.staleAfterMs;
}

/**
 * Staff tap "Appeler sur WhatsApp": `not_callable` unless the guest can be called, `call_in_progress` while an attempt
 * is ringing. Otherwise the new attempt, which also replaces a pending automatic call.
 */
export function startCall(t: Pick<TicketState, 'status' | 'channel' | 'phone'> & { call?: CallState | null }, trigger: CallTrigger, now: number): CallState {
  if (!canCallGuest(t)) throw new TicketError('not_callable');
  if (callInProgress(t.call, now)) throw new TicketError('call_in_progress');
  return { status: 'calling', at: now, attempt: (t.call?.attempt ?? 0) + 1, trigger, reason: null };
}

/** A report from the SIP server's dialplan: `answered` when the guest picked up, `ended` when the call is over. */
export interface CallReport {
  attempt: number;
  event: 'answered' | 'ended';
  /** `ended` only: whether the guest picked up during the call. */
  answered?: boolean;
  /** `ended` only: the Q.850 hang-up cause the SIP server saw (16 normal, 17 busy, 19 no answer, 21 rejected...). */
  cause?: number | null;
}

/** Hang-up causes that mean the guest's phone was reached but nobody picked up (busy, no answer, declined, unreachable). */
const NOT_ANSWERED_CAUSES = new Set([16, 17, 18, 19, 20, 31]);

/**
 * The call state after a report, or null when the report is stale (an older attempt, or the call is no longer under
 * way). An answered call stays `answered` when it ends; an unanswered one ends as `not_answered`, or `failed` when Meta
 * refused it (403 maps to cause 21, "call rejected", which is also what a missing call permission gives).
 */
export function applyCallReport(call: CallState | null | undefined, report: CallReport): CallState | null {
  if (!call || report.attempt !== call.attempt) return null;
  if (call.status !== 'calling' && call.status !== 'answered') return null;
  if (report.event === 'answered') return call.status === 'calling' ? { ...call, status: 'answered' } : null;
  if (report.answered || call.status === 'answered') return { ...call, status: 'answered', reason: report.cause == null ? null : String(report.cause) };
  const cause = report.cause ?? null;
  const status = cause !== null && NOT_ANSWERED_CAUSES.has(cause) ? 'not_answered' : 'failed';
  return { ...call, status, reason: cause === null ? null : String(cause) };
}

/** The attempt could not start (no permission, SIP server refused the request): what staff see. */
export function failCall(call: CallState, status: 'failed' | 'no_permission', reason: string): CallState {
  return { ...call, status, reason };
}

export function callView(call: CallState | null | undefined): CallView | null {
  return call ? { status: call.status, at: call.at } : null;
}

/** What the staff row shows about the call (rules shared by the screen and its tests). */
export interface CallRowState {
  /** The button "Appeler sur WhatsApp": shown on ready WhatsApp rows at the counter, disabled while a call rings. */
  button: 'hidden' | 'enabled' | 'disabled';
  /** The line under the row: the countdown, the attempt's state, or the reminder on a waiting WhatsApp row. */
  line:
    | { kind: 'countdown'; secondsLeft: number }
    | { kind: 'status'; status: Exclude<CallStatus, 'scheduled'> }
    | { kind: 'after_ready'; minutes: number }
    | null;
}

export function callRowState(o: { status: 'waiting' | 'ready'; channel: Channel | string; call?: CallView | null }, view: 'counter' | 'kitchen', now: number): CallRowState {
  const whatsapp = o.channel === 'whatsapp';
  if (!whatsapp) return { button: 'hidden', line: null };
  if (o.status === 'waiting') return { button: 'hidden', line: view === 'counter' ? { kind: 'after_ready', minutes: CALL.autoAfterReadyMs / 60_000 } : null };
  const button = view === 'kitchen' ? 'hidden' : o.call?.status === 'calling' && now - o.call.at < CALL.staleAfterMs ? 'disabled' : 'enabled';
  if (!o.call) return { button, line: null };
  if (o.call.status === 'scheduled') return { button, line: { kind: 'countdown', secondsLeft: Math.max(0, Math.ceil((o.call.at - now) / 1000)) } };
  return { button, line: { kind: 'status', status: o.call.status } };
}

/** "4:05": minutes and seconds of a countdown, digits only (an LTR run in Arabic). */
export function formatCountdown(secondsLeft: number): string {
  const s = Math.max(0, Math.floor(secondsLeft));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
